import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FiUsers, FiMessageSquare, FiActivity, FiBarChart2, FiAlertCircle, FiInfo } from 'react-icons/fi';
import { getAdminStats } from '../../services/adminService';
import ExtensionBanner from '../../components/ExtensionBanner';
import Tooltip from '../../components/Tooltip';

const AdminDashboard = () => {
  const [stats, setStats] = useState({
    total_users: 0,
    premium_users: 0,
    active_users_last_30_days: 0,
    open_tickets: 0,
    total_resumes: 0,
    total_cover_letters: 0,
    total_applications: 0,
    polish_usage_this_week: 0,
    total_credits_available: 0,
    total_lifetime_credits: 0
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        setLoading(true);
        const response = await getAdminStats();
        setStats(response.data);
        setLoading(false);
      } catch (err) {
        console.error('Error fetching admin stats:', err);
        setError('Failed to load dashboard data. Please try again later.');
        setLoading(false);
      }
    };

    fetchStats();
  }, []);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border-l-4 border-red-400 p-4 mb-4">
        <div className="flex">
          <div className="flex-shrink-0">
            <FiAlertCircle className="h-5 w-5 text-red-400" />
          </div>
          <div className="ml-3">
            <p className="text-sm text-red-700">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-6xl mx-auto">
      {/* Extension Banner */}
      <ExtensionBanner />
      
      <h1 className="text-2xl font-semibold text-gray-900">Admin Dashboard</h1>
      
      {/* Stats Overview */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0 bg-primary-500 rounded-md p-3">
                <FiUsers className="h-6 w-6 text-white" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Total Users</dt>
                  <dd className="text-lg font-semibold text-gray-900">{stats.total_users}</dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0 bg-green-500 rounded-md p-3">
                <FiActivity className="h-6 w-6 text-white" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Premium Users</dt>
                  <dd className="text-lg font-semibold text-gray-900">{stats.premium_users}</dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0 bg-blue-500 rounded-md p-3">
                <FiBarChart2 className="h-6 w-6 text-white" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Active Users (30 Days)</dt>
                  <dd className="text-lg font-semibold text-gray-900">{stats.active_users_last_30_days}</dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0 bg-yellow-500 rounded-md p-3">
                <FiMessageSquare className="h-6 w-6 text-white" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Open Tickets</dt>
                  <dd className="text-lg font-semibold text-gray-900">{stats.open_tickets}</dd>
                </dl>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Document Stats */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-3 mt-6">
        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0 bg-primary-500 rounded-md p-3">
                <FiBarChart2 className="h-6 w-6 text-white" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Total Resumes</dt>
                  <dd className="text-lg font-semibold text-gray-900">{stats.total_resumes}</dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0 bg-primary-500 rounded-md p-3">
                <FiBarChart2 className="h-6 w-6 text-white" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Total Cover Letters</dt>
                  <dd className="text-lg font-semibold text-gray-900">{stats.total_cover_letters}</dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0 bg-teal-500 rounded-md p-3">
                <FiBarChart2 className="h-6 w-6 text-white" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Total Applications</dt>
                  <dd className="text-lg font-semibold text-gray-900">{stats.total_applications}</dd>
                </dl>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Quick Links */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="px-6 py-6 sm:p-8">
            <div className="flex items-center">
              <h3 className="text-lg leading-6 font-medium text-gray-900">User Management</h3>
              <Tooltip text="Manage user accounts and permissions" position="right">
                <FiInfo className="ml-2 text-gray-400" />
              </Tooltip>
            </div>
            <div className="mt-2 max-w-xl text-sm text-gray-500">
              <p>View and manage user accounts, update membership types, and monitor user activity.</p>
            </div>
            <div className="mt-5">
              <Tooltip text="Go to user management page" position="bottom">
                <Link
                  to="/admin/users"
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                >
                  Manage Users
                </Link>
              </Tooltip>
            </div>
          </div>
        </div>
        
        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="px-6 py-6 sm:p-8">
            <div className="flex items-center">
              <h3 className="text-lg leading-6 font-medium text-gray-900">Polish Usage Statistics</h3>
              <Tooltip text="Usage statistics for resume polish feature" position="right">
                <FiInfo className="ml-2 text-gray-400" />
              </Tooltip>
            </div>
            <div className="mt-2">
              <p className="text-3xl font-bold text-gray-900">{stats.polish_usage_this_week}</p>
            </div>
            <div className="mt-2 max-w-xl text-sm text-gray-500">
              <p>Number of resume polish operations used in the past week.</p>
            </div>
            <div className="mt-6 grid grid-cols-2 gap-6">
              <div className="bg-gray-50 p-4 rounded-lg">
                <div className="flex items-center">
                  <p className="text-sm font-medium text-gray-500">Total Credits Available</p>
                  <Tooltip text="Sum of all users' available credits" position="top">
                    <FiInfo className="ml-1 text-gray-400" />
                  </Tooltip>
                </div>
                <p className="text-xl font-semibold text-gray-900 mt-1">{stats.total_credits_available}</p>
              </div>
              <div className="bg-gray-50 p-4 rounded-lg">
                <div className="flex items-center">
                  <p className="text-sm font-medium text-gray-500">Total Lifetime Credits</p>
                  <Tooltip text="Sum of all credits ever issued" position="top">
                    <FiInfo className="ml-1 text-gray-400" />
                  </Tooltip>
                </div>
                <p className="text-xl font-semibold text-gray-900 mt-1">{stats.total_lifetime_credits}</p>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="px-6 py-6 sm:p-8">
            <div className="flex items-center">
              <h3 className="text-lg leading-6 font-medium text-gray-900">Ticket Management</h3>
              <Tooltip text="Handle user support requests" position="right">
                <FiInfo className="ml-2 text-gray-400" />
              </Tooltip>
            </div>
            <div className="mt-2 max-w-xl text-sm text-gray-500">
              <p>Respond to support tickets, track issues, and provide assistance to users.</p>
            </div>
            <div className="mt-5">
              <Tooltip text="Go to ticket management page" position="bottom">
                <Link
                  to="/admin/tickets"
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                >
                  Manage Tickets
                </Link>
              </Tooltip>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;